import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Download } from "lucide-react";
import { getInsight, INSIGHTS } from "@/data/insights";

const GOLD = "#C5A24A";
const CONTACT_HREF = "mailto:iainmunro@zentaraeng.com?subject=Inspection%20enquiry";
const API = process.env.REACT_APP_BACKEND_URL;

function Block({ b }) {
  if (b.type === "h2")
    return <h2 className="font-display text-2xl font-bold mt-10 mb-3 tracking-tight">{b.text}</h2>;
  if (b.type === "quote" || b.type === "callout")
    return (
      <p className="my-8 border-l-2 pl-5 py-1 text-lg text-white font-medium leading-relaxed" style={{ borderColor: GOLD }}>
        {b.text}
      </p>
    );
  if (b.type === "list")
    return (
      <ul className="my-5 space-y-2.5">
        {b.items.map((it) => (
          <li key={it} className="flex items-start gap-3 text-[#D4D4D8] leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: GOLD }} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  if (b.type === "table")
    return (
      <div className="my-8 overflow-x-auto rounded-xl border border-[#27272A]">
        <table className="w-full text-left text-sm min-w-[640px]">
          <thead>
            <tr className="bg-[#18181B]">
              {b.head.map((h) => (
                <th key={h} className="px-4 py-3 font-semibold text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {b.rows.map((r, ri) => (
              <tr key={ri} className="border-t border-[#27272A] align-top">
                {r.map((cell, ci) => (
                  <td key={ci} className={ci === 0 ? "px-4 py-3 font-semibold text-white" : "px-4 py-3 text-[#A1A1AA]"}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  return <p className="my-4 text-[#C7C7CC] leading-[1.8]">{b.text}</p>;
}

export default function InsightArticle() {
  const { slug } = useParams();
  const article = getInsight(slug);
  if (!article) return <Navigate to="/insights" replace />;
  const others = INSIGHTS.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-[#09090B] text-[#FAFAFA] grain">
      <header className="sticky top-0 z-40 h-16 border-b border-[#27272A] bg-[#09090B]/90 backdrop-blur flex items-center justify-between px-6 md:px-12">
        <Link to="/" className="flex items-center gap-2">
          <img src="/zentara-logo.png" alt="Zentara Integrity" className="w-8 h-8 object-contain" />
          <span className="font-display font-bold text-lg">Zentara <span className="text-[#C5A24A]">Integrity</span></span>
        </Link>
        <a href={CONTACT_HREF} className="text-sm font-semibold px-4 py-2 rounded-md text-[#09090B] hover:-translate-y-0.5 transition-transform" style={{ background: GOLD }} data-testid="article-request">
          Request an inspection
        </a>
      </header>

      <article className="max-w-3xl mx-auto px-6 md:px-8 pt-12 pb-8">
        <Link to="/insights" className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors mb-8" data-testid="article-back">
          <ArrowLeft className="w-4 h-4" /> All insights
        </Link>
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-[#71717A]">
          <span style={{ color: GOLD }}>{article.tag}</span><span>·</span><span>{article.date}</span><span>·</span><span>{article.reading}</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] mt-3" data-testid="article-title">{article.title}</h1>
        <p className="mt-5 text-lg text-[#A1A1AA] leading-relaxed">{article.excerpt}</p>
        {article.hasPdf && (
          <a href={`${API}/api/insights/${article.slug}/pdf`} target="_blank" rel="noreferrer"
            className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md text-sm font-semibold border transition-colors hover:bg-[#18181B]"
            style={{ borderColor: GOLD, color: GOLD }} data-testid="article-download-pdf">
            <Download className="w-4 h-4" /> Download one-pager (PDF)
          </a>
        )}
      </article>

      <div className="max-w-4xl mx-auto px-6 md:px-8">
        <div className="rounded-xl overflow-hidden border border-[#27272A]">
          <img src={article.hero} alt={article.title} className="w-full h-64 md:h-80 object-cover" />
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="max-w-3xl mx-auto px-6 md:px-8 py-12 text-[17px]">
        {article.body.map((b, i) => <Block key={i} b={b} />)}

        <div className="mt-12 border border-[#27272A] bg-[#18181B] rounded-xl p-7">
          <h3 className="font-display text-xl font-bold">Turn your structure into an evidence trail.</h3>
          <p className="text-[#A1A1AA] mt-2 text-sm">Book a Zentara inspection and receive a professional, engineer-grade report you can compare against every future visit.</p>
          <a href={CONTACT_HREF} className="mt-5 inline-flex items-center gap-2 h-11 px-6 rounded-md font-semibold text-[#09090B] hover:-translate-y-0.5 transition-transform" style={{ background: GOLD }} data-testid="article-cta">
            Request an inspection <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </motion.div>

      {others.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 md:px-8 pb-24">
          <p className="label-caps mb-5" style={{ color: GOLD }}>Keep reading</p>
          <div className="grid sm:grid-cols-2 gap-5">
            {others.map((a) => (
              <Link key={a.slug} to={`/insights/${a.slug}`} className="group block border border-[#27272A] bg-[#18181B] rounded-xl p-5 hover:border-[#C5A24A]/40 transition-colors" data-testid={`related-${a.slug}`}>
                <span className="text-[11px] uppercase tracking-wider" style={{ color: GOLD }}>{a.tag}</span>
                <h4 className="font-display font-bold mt-1 group-hover:text-white">{a.title}</h4>
                <p className="text-sm text-[#A1A1AA] mt-1.5 line-clamp-2">{a.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
